import { useState } from 'react';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import type {
  ConditionalFormatRule,
  ConditionalFormatType,
  ThresholdConfig,
  ThresholdCondition,
  DataBarConfig,
  ColorScaleConfig,
} from '../../types/reportBuilder';
import { X, Plus, PaintBrush } from '@phosphor-icons/react';

const FORMAT_TYPE_LABELS: Record<ConditionalFormatType, string> = {
  threshold: 'Threshold',
  data_bar: 'Data Bar',
  color_scale: 'Color Scale',
};

function defaultThresholdConfig(): ThresholdConfig {
  return {
    conditions: [
      { operator: 'gte', value: 80, color: '#166534', bgColor: '#dcfce7' },
      { operator: 'gte', value: 50, color: '#854d0e', bgColor: '#fef9c3' },
    ],
    defaultBgColor: '#fee2e2',
  };
}

function defaultDataBarConfig(): DataBarConfig {
  return { color: '#93c5fd' };
}

function defaultColorScaleConfig(): ColorScaleConfig {
  return { minColor: '#fee2e2', midColor: '#fef9c3', maxColor: '#dcfce7' };
}

function ThresholdEditor({ config, onChange }: { config: ThresholdConfig; onChange: (c: ThresholdConfig) => void }) {
  const updateCondition = (idx: number, partial: Partial<ThresholdCondition>) => {
    const updated = [...config.conditions];
    updated[idx] = { ...updated[idx], ...partial };
    onChange({ ...config, conditions: updated });
  };

  const removeCondition = (idx: number) => {
    onChange({ ...config, conditions: config.conditions.filter((_, i) => i !== idx) });
  };

  const addCondition = () => {
    onChange({ ...config, conditions: [...config.conditions, { operator: 'gte', value: 0, color: '#1e3a5f', bgColor: '#dbeafe' }] });
  };

  return (
    <div className="space-y-2">
      <label className="block text-[10px] font-medium text-gray-500">Conditions (evaluated top-to-bottom, first match wins)</label>
      {config.conditions.map((cond, i) => (
        <div key={i} className="flex items-center gap-1">
          <select value={cond.operator} onChange={e => updateCondition(i, { operator: e.target.value as ThresholdCondition['operator'] })}
            className="w-16 rounded border border-gray-300 px-1 py-1 text-[10px]">
            <option value="gt">&gt;</option>
            <option value="gte">&gt;=</option>
            <option value="lt">&lt;</option>
            <option value="lte">&lt;=</option>
            <option value="eq">=</option>
          </select>
          <input type="number" value={cond.value} onChange={e => updateCondition(i, { value: Number(e.target.value) })}
            className="w-16 rounded border border-gray-300 px-1 py-1 text-[10px]" />
          <label className="text-[9px] text-gray-400">bg</label>
          <input type="color" value={cond.bgColor} onChange={e => updateCondition(i, { bgColor: e.target.value })}
            className="h-5 w-6 cursor-pointer rounded border border-gray-200 p-0" />
          <label className="text-[9px] text-gray-400">text</label>
          <input type="color" value={cond.color} onChange={e => updateCondition(i, { color: e.target.value })}
            className="h-5 w-6 cursor-pointer rounded border border-gray-200 p-0" />
          <button onClick={() => removeCondition(i)} className="rounded p-0.5 text-gray-400 hover:bg-gray-100"><X size={10} /></button>
        </div>
      ))}
      <button onClick={addCondition} className="flex items-center gap-0.5 text-[10px] text-blue-600 hover:text-blue-800">
        <Plus size={10} /> Add condition
      </button>
      <div className="flex items-center gap-1">
        <label className="text-[10px] text-gray-500">Default background:</label>
        <input type="color" value={config.defaultBgColor} onChange={e => onChange({ ...config, defaultBgColor: e.target.value })}
          className="h-5 w-6 cursor-pointer rounded border border-gray-200 p-0" />
      </div>
    </div>
  );
}

function DataBarEditor({ config, onChange }: { config: DataBarConfig; onChange: (c: DataBarConfig) => void }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <label className="text-[10px] text-gray-500">Bar color:</label>
        <input type="color" value={config.color} onChange={e => onChange({ ...config, color: e.target.value })}
          className="h-5 w-6 cursor-pointer rounded border border-gray-200 p-0" />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[10px] text-gray-500">Max value (optional):</label>
        <input type="number" value={config.maxValue ?? ''} onChange={e => onChange({ ...config, maxValue: e.target.value ? Number(e.target.value) : undefined })}
          placeholder="auto" className="w-20 rounded border border-gray-300 px-1 py-1 text-[10px]" />
      </div>
    </div>
  );
}

function ColorScaleEditor({ config, onChange }: { config: ColorScaleConfig; onChange: (c: ColorScaleConfig) => void }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <label className="text-[10px] text-gray-500">Min:</label>
          <input type="color" value={config.minColor} onChange={e => onChange({ ...config, minColor: e.target.value })}
            className="h-5 w-6 cursor-pointer rounded border border-gray-200 p-0" />
        </div>
        <div className="flex items-center gap-1">
          <label className="text-[10px] text-gray-500">Mid:</label>
          <input type="color" value={config.midColor ?? '#ffffff'} onChange={e => onChange({ ...config, midColor: e.target.value })}
            className="h-5 w-6 cursor-pointer rounded border border-gray-200 p-0" />
        </div>
        <div className="flex items-center gap-1">
          <label className="text-[10px] text-gray-500">Max:</label>
          <input type="color" value={config.maxColor} onChange={e => onChange({ ...config, maxColor: e.target.value })}
            className="h-5 w-6 cursor-pointer rounded border border-gray-200 p-0" />
        </div>
      </div>
      <div className="h-3 w-full rounded" style={{
        background: `linear-gradient(to right, ${config.minColor}, ${config.midColor ?? '#ffffff'}, ${config.maxColor})`,
      }} />
    </div>
  );
}

export default function ConditionalFormatPanel({ onClose }: { onClose: () => void }) {
  const { results, conditionalFormats, addConditionalFormat, removeConditionalFormat } = useReportBuilderStore();

  const [column, setColumn] = useState('');
  const [formatType, setFormatType] = useState<ConditionalFormatType>('threshold');
  const [config, setConfig] = useState<ThresholdConfig | DataBarConfig | ColorScaleConfig>(defaultThresholdConfig());

  const measureColumns = results?.columns.filter(c => c.is_measure) ?? [];

  const handleTypeChange = (type: ConditionalFormatType) => {
    setFormatType(type);
    switch (type) {
      case 'threshold': setConfig(defaultThresholdConfig()); break;
      case 'data_bar': setConfig(defaultDataBarConfig()); break;
      case 'color_scale': setConfig(defaultColorScaleConfig()); break;
    }
  };

  const handleAdd = () => {
    if (!column) return;
    const rule: ConditionalFormatRule = {
      id: crypto.randomUUID(),
      column,
      type: formatType,
      config,
    };
    addConditionalFormat(rule);
    // Reset for next rule
    setColumn('');
    setConfig(defaultThresholdConfig());
    setFormatType('threshold');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="w-[460px] max-h-[80vh] overflow-y-auto rounded-lg bg-white shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
            <PaintBrush size={14} /> Conditional Formatting
          </h3>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100"><X size={14} /></button>
        </div>

        {/* Existing rules */}
        {conditionalFormats.length > 0 && (
          <div className="border-b px-4 py-2 space-y-1">
            <label className="block text-[10px] font-medium text-gray-500">Active Rules</label>
            {conditionalFormats.map(rule => (
              <div key={rule.id} className="flex items-center justify-between rounded border border-gray-200 px-2 py-1">
                <span className="text-xs text-gray-700">
                  {rule.column} — {FORMAT_TYPE_LABELS[rule.type]}
                </span>
                <button onClick={() => removeConditionalFormat(rule.id)} className="rounded p-0.5 text-gray-400 hover:bg-gray-100">
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add new rule */}
        <div className="space-y-3 px-4 py-3">
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Column</label>
            <select value={column} onChange={e => setColumn(e.target.value)}
              className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none">
              <option value="">Select a measure column...</option>
              {measureColumns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Format Type</label>
            <div className="flex gap-1">
              {(['threshold', 'data_bar', 'color_scale'] as ConditionalFormatType[]).map(t => (
                <button key={t} onClick={() => handleTypeChange(t)}
                  className={`rounded border px-2 py-1 text-[10px] ${formatType === t ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  {FORMAT_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          {formatType === 'threshold' && <ThresholdEditor config={config as ThresholdConfig} onChange={setConfig} />}
          {formatType === 'data_bar' && <DataBarEditor config={config as DataBarConfig} onChange={setConfig} />}
          {formatType === 'color_scale' && <ColorScaleEditor config={config as ColorScaleConfig} onChange={setConfig} />}
        </div>

        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <button onClick={onClose} className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50">Close</button>
          <button onClick={handleAdd} disabled={!column}
            className="rounded bg-primary px-3 py-1.5 text-xs text-white hover:bg-primary/90 disabled:opacity-50">
            Add Rule
          </button>
        </div>
      </div>
    </div>
  );
}
